import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import algoliasearch from "algoliasearch";
import { InstantSearch, Hits, Configure } from "react-instantsearch";
import Authenticated from "../components/Layouts/Authenticated";
import { FiltersIcon } from "../components/ui";
import {
  RecipeCard,
  RecipeFilters,
  SearchBox,
  CustomPagination,
  RecipeHit,
} from "../components/RecipeDirectory";
import { theme } from "../theme";
import { userDirectoryStyles, recipeDirectoryStyles } from "./styles";

/**
 * MaterialsDatabase Page Component
 *
 * A searchable directory of materials with filtering capabilities.
 * Uses Algolia InstantSearch for real-time search functionality.
 */
const MaterialsDatabase: React.FC = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [filtersOpen, setFiltersOpen] = useState(!isMobile);

  // Update filters visibility based on screen size
  useEffect(() => {
    setFiltersOpen(!isMobile);
  }, [isMobile]);

  // Initialize Algolia search client
  const searchClient = algoliasearch(
    process.env.REACT_APP_ALGOLIA_APP_ID || "",
    process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY || ""
  );
  const indexName =
    process.env.REACT_APP_ALGOLIA_RECIPES_INDEX || "dev-commons-recipes-index";

  const toggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  };

  // Custom hit component wrapper for RecipeCard
  const HitComponent = ({ hit }: { hit: RecipeHit }) => (
    <RecipeCard hit={hit} />
  );

  return (
    <Authenticated>
      <title>Materials Database - Materiom Commons</title>
      <Container maxWidth="xl" sx={userDirectoryStyles.container}>
        <InstantSearch searchClient={searchClient} indexName={indexName}>
          <Configure hitsPerPage={24} distinct={true} />
          <Grid container spacing={3}>
            {/* Filters Panel */}
            <Grid size={{ xs: 12, lg: 3 }}>
              <Paper
                elevation={userDirectoryStyles.paper.elevation}
                sx={{
                  borderRadius: userDirectoryStyles.paper.borderRadius,
                  overflow: userDirectoryStyles.paper.overflow,
                }}
              >
                {/* Filters Header */}
                <Box
                  sx={{
                    ...userDirectoryStyles.filtersHeader,
                    cursor: isMobile ? "pointer" : "default",
                  }}
                  onClick={isMobile ? toggleFilters : undefined}
                >
                  <Box sx={userDirectoryStyles.filtersHeaderContent}>
                    <FiltersIcon sx={userDirectoryStyles.filtersIcon} />
                    <Box sx={userDirectoryStyles.filtersHeaderRow}>
                      <Typography
                        variant="h6"
                        sx={userDirectoryStyles.filtersTitle}
                      >
                        Filters
                      </Typography>
                      {isMobile && (
                        <Typography
                          variant="body2"
                          sx={userDirectoryStyles.filtersToggleText}
                        >
                          {filtersOpen ? "Hide" : "Show"} filters
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Filters Content */}
                <Collapse in={filtersOpen}>
                  <Box sx={userDirectoryStyles.filtersContent}>
                    {/* Recipe Filters */}
                    <RecipeFilters />
                  </Box>
                </Collapse>
              </Paper>
            </Grid>

            {/* Results Panel */}
            <Grid size={{ xs: 12, lg: 9 }}>
              <Paper
                elevation={userDirectoryStyles.paper.elevation}
                sx={{
                  borderRadius: userDirectoryStyles.paper.borderRadius,
                  overflow: userDirectoryStyles.paper.overflow,
                  minHeight: "calc(100vh - 180px)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Search Header */}
                <Box sx={recipeDirectoryStyles.searchHeader}>
                  <SearchBox
                    placeholder="Search for anything"
                    sx={recipeDirectoryStyles.searchBox}
                    showSearchButton
                    trackingEventName="materials-database-search"
                  />
                </Box>

                {/* Results Content - Grid of Recipe Cards */}
                <Box sx={recipeDirectoryStyles.resultsContent}>
                  <Hits
                    hitComponent={HitComponent}
                    classNames={{
                      list: "recipe-hits-grid",
                      item: "recipe-hits-item",
                    }}
                  />
                </Box>

                {/* Pagination */}
                <Box sx={userDirectoryStyles.paginationContainer}>
                  <CustomPagination />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </InstantSearch>
      </Container>
    </Authenticated>
  );
};

export default MaterialsDatabase;
